import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { reviewApi } from '../../utils/reviewApi';
import { ItemDetails } from '../../components/RecordFields';
import CommentThread from './CommentThread';
import CompareView, { PdfRenditionView } from './CompareView';
import { applyHighlights, focusHighlight, selectionAnchor } from './docAnchors';
import { FileChip, fmtDate } from './reviewUi';
import '../../styles/DocReview.css';

const TABS = [
  ['all', 'All'],
  ['open', 'Open'],
  ['mine', 'Mine'],
  ['document', 'Whole document'],
  ['earlier', 'Earlier rounds'],
];

/** The Word view — sanitised HTML, highlights applied after every comment change. */
function WordView({ html, comments, focusId, onPick, onSelect, onMissing }) {
  const ref = useRef(null);
  const clean = useMemo(() => DOMPurify.sanitize(html || '', { FORBID_TAGS: ['style', 'form'], FORBID_ATTR: ['style'] }), [html]);
  const textComments = useMemo(() => comments.filter((c) => c.anchor_type === 'text' && !c.deleted && !c.parent_id), [comments]);

  useEffect(() => {
    if (!ref.current) return;
    onMissing(applyHighlights(ref.current, textComments, onPick));
  }, [clean, textComments, onPick, onMissing]);

  useEffect(() => {
    if (ref.current && focusId) focusHighlight(ref.current, focusId);
  }, [focusId, textComments]);

  const onMouseUp = () => {
    const a = ref.current ? selectionAnchor(ref.current) : null;
    onSelect(a);
  };

  return (
    <article className="drv-page-doc" ref={ref} onMouseUp={onMouseUp}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: clean }} />
  );
}

/** The Excel view — a grid per sheet; click a cell to comment on it. */
function ExcelView({ sheets, comments, focusId, onCell }) {
  const [tab, setTab] = useState(0);
  const sheet = sheets[tab] || { rows: [], columns: [] };
  const marked = useMemo(() => {
    const m = {};
    comments.filter((c) => c.anchor_type === 'cell' && !c.deleted && !c.parent_id && c.anchor.sheet === sheet.name)
      .forEach((c) => { m[c.anchor.cell] = m[c.anchor.cell] || []; m[c.anchor.cell].push(c); });
    return m;
  }, [comments, sheet.name]);
  const focusCell = comments.find((c) => c.id === focusId && c.anchor_type === 'cell');
  useEffect(() => {
    if (focusCell && focusCell.anchor.sheet !== sheet.name) {
      const i = sheets.findIndex((s) => s.name === focusCell.anchor.sheet);
      if (i >= 0) setTab(i);
    }
  }, [focusCell, sheet.name, sheets]);

  return (
    <div className="drv-xl">
      <div className="drv-xl-scroll">
        <table className="drv-xl-grid">
          <thead><tr><th aria-label="Row" />{sheet.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {sheet.rows.map((row, r) => (
              <tr key={r}>
                <th>{r + 1}</th>
                {row.map((v, j) => {
                  const ref = `${sheet.columns[j]}${r + 1}`;
                  const cs = marked[ref];
                  const on = focusCell && focusCell.anchor.sheet === sheet.name && focusCell.anchor.cell === ref;
                  return (
                    <td key={j} className={`${cs ? 'drv-xl-c' : ''}${on ? ' drv-xl-on' : ''}`}
                      title={cs ? `${cs.length} comment${cs.length === 1 ? '' : 's'} — click to add another` : 'Click to comment on this cell'}
                      onClick={() => onCell(sheet.name, ref, v, cs?.[0]?.id)}>
                      {v}
                      {cs && <span className="drv-xl-mark" aria-hidden="true" />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sheets.length > 1 && (
        <div className="drv-xl-tabs" role="tablist">
          {sheets.map((s, i) => (
            <button type="button" role="tab" aria-selected={i === tab} key={s.name}
              className={`drv-xl-tab${i === tab ? ' drv-xl-tab--on' : ''}`} onClick={() => setTab(i)}>{s.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The reviewer's screen (mockup screens 4–6): the document on the left, comments on the right.
 *
 *   Word  → the text; select it → Comment / Suggest change
 *   Excel → the cell grid; click a cell
 *   PDF   → the browser's own viewer; comments carry a page number
 *   other → download it; comments are on the whole document, with an optional "where"
 *
 * Beside the document (v4.11.0, as in the Orcanos QMS viewer): **PDF** — the draft as Orcanos'
 * own PDF rendition — and **Compare** — track changes against an earlier revision, on the PDFs.
 *
 * The file is the ROUND's snapshot, so what you comment on is what everyone else sees.
 */
export default function ReviewDocPage() {
  const { reviewId } = useParams();
  const [params, setParams] = useSearchParams();
  const roundParam = params.get('round');
  const [review, setReview] = useState(null);
  const [doc, setDoc] = useState(null);
  const [docError, setDocError] = useState('');
  const [comments, setComments] = useState([]);
  // Earlier rounds' comments — listed, never highlighted (they were written on another draft).
  const [earlier, setEarlier] = useState([]);
  const [canComment, setCanComment] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [side, setSide] = useState('comments');
  const [focusId, setFocusId] = useState('');
  const [missing, setMissing] = useState([]);
  const [selection, setSelection] = useState(null);   // {anchor, rect}
  const [draft, setDraft] = useState(null);           // {kind, anchor_type, anchor, label}
  const [draftBody, setDraftBody] = useState('');
  const [draftChange, setDraftChange] = useState('');
  const [general, setGeneral] = useState('');
  const [where, setWhere] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('doc');            // doc | pdf | compare
  const docAreaRef = useRef(null);
  const listRef = useRef(null);

  const round = roundParam ? Number(roundParam) : undefined;
  useEffect(() => { setView('doc'); }, [reviewId, round]);

  const loadReview = useCallback(async () => {
    const { review: rv } = await reviewApi.review(reviewId);
    setReview(rv);
    return rv;
  }, [reviewId]);

  // Stable per review: ItemDetails re-runs its load whenever this identity changes, and App
  // re-renders every second — an inline arrow cancelled the request forever (Critical Note #59).
  const loadForm = useCallback(() => reviewApi.itemForm(reviewId), [reviewId]);

  const loadComments = useCallback(async () => {
    const d = await reviewApi.comments(reviewId, round);
    setComments(d.comments || []);
    setEarlier(d.earlier || []);
    setCanComment(!!d.can_comment);
  }, [reviewId, round]);

  useEffect(() => {
    let live = true;
    setDoc(null);
    setDocError('');
    (async () => {
      try {
        await loadReview();
        await loadComments();
      } catch (e) {
        if (live) setError(e.status === 404 ? 'This review no longer exists, or belongs to another account.' : e.message);
        return;
      }
      try {
        const d = await reviewApi.document(reviewId, round);
        if (live) setDoc(d);
      } catch (e) {
        if (live) setDocError(e.message);
      }
    })();
    return () => { live = false; };
  }, [reviewId, round, loadReview, loadComments]);

  // Comments are re-read when the tab comes back into focus — not on a timer: a background
  // poll through an authenticated route would keep the session alive forever (Note #69).
  useEffect(() => {
    const onFocus = () => { loadComments().catch(() => {}); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadComments]);

  // The PDF viewer gets a blob: URL, not /api/* — an iframe src is a navigation too, and the dev
  // proxy would answer it with the app itself.
  const [pdfUrl, setPdfUrl] = useState('');
  const pdfRound = doc?.file_kind === 'pdf' ? (round || review?.current_round) : null;
  useEffect(() => {
    if (!pdfRound) return undefined;
    let live = true;
    let url = '';
    reviewApi.fileBlob(reviewId, pdfRound)
      .then(({ blob }) => {
        if (!live) return;
        url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch((e) => { if (live) setDocError(e.message); });
    return () => { live = false; setPdfUrl(''); if (url) URL.revokeObjectURL(url); };
  }, [reviewId, pdfRound]);

  const download = async (roundNo, withComments = false) => {
    try {
      const { blob, name } = await reviewApi.fileBlob(reviewId, roundNo, withComments);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.message);
    }
  };

  const tops = useMemo(() => comments.filter((c) => !c.parent_id), [comments]);
  const earlierTops = useMemo(() => earlier.filter((c) => !c.parent_id).sort((a, b) => a.round_no - b.round_no), [earlier]);
  const repliesOf = useMemo(() => {
    const m = {};
    [...comments, ...earlier].filter((c) => c.parent_id).forEach((c) => { (m[c.parent_id] = m[c.parent_id] || []).push(c); });
    return m;
  }, [comments, earlier]);
  const shown = tab === 'earlier' ? earlierTops : tops.filter((c) => {
    if (tab === 'open') return c.status === 'open' && !c.deleted;
    if (tab === 'mine') return c.can_edit;
    if (tab === 'document') return c.anchor_type === 'document';
    return true;
  });
  const counts = {
    all: tops.filter((c) => !c.deleted).length,
    open: tops.filter((c) => c.status === 'open' && !c.deleted).length,
    mine: tops.filter((c) => c.can_edit).length,
    document: tops.filter((c) => c.anchor_type === 'document' && !c.deleted).length,
    earlier: earlierTops.filter((c) => !c.deleted).length,
  };

  const pick = useCallback((cid) => {
    setFocusId(cid);
    setSide('comments');
    setTab((t) => (t === 'document' || t === 'earlier' ? 'all' : t));
    setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-cid="${cid}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }, []);
  const onMissing = useCallback((ids) => {
    setMissing((prev) => (prev.length === ids.length && prev.every((x, i) => x === ids[i]) ? prev : ids));
  }, []);

  const onSelect = (a) => {
    if (!a || !canComment) { setSelection(null); return; }
    const area = docAreaRef.current?.getBoundingClientRect();
    setSelection({ anchor: { selected_text: a.selected_text, before: a.before, after: a.after },
      top: a.rect.top - (area?.top || 0) + (docAreaRef.current?.scrollTop || 0) - 46,
      left: Math.max(8, a.rect.left - (area?.left || 0) + a.rect.width / 2 - 110) });
  };

  const startDraft = (d) => {
    setDraft(d);
    setDraftBody('');
    setDraftChange(d.kind === 'change' ? d.anchor.selected_text : '');
    setSelection(null);
    setSide('comments');
    window.getSelection()?.removeAllRanges();
  };

  const submitDraft = async () => {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      const { comment } = await reviewApi.addComment(reviewId, {
        kind: draft.kind, anchor_type: draft.anchor_type, anchor: draft.anchor,
        body: draftBody.trim(), suggested_text: draft.kind === 'change' ? draftChange : '',
      });
      setDraft(null);
      await loadComments();
      setFocusId(comment.id);
      loadReview().catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitGeneral = async () => {
    const kind = doc?.file_kind;
    const anchor = kind === 'pdf' && where.trim()
      ? { page: where.trim() } : where.trim() ? { where: where.trim() } : {};
    setBusy(true);
    setError('');
    try {
      await reviewApi.addComment(reviewId, {
        kind: 'comment', anchor_type: kind === 'pdf' && where.trim() ? 'page' : 'document', anchor, body: general.trim(),
      });
      setGeneral('');
      setWhere('');
      await loadComments();
      loadReview().catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onAction = async (what, c, body) => {
    setError('');
    try {
      if (what === 'reply') await reviewApi.addComment(reviewId, { body: body.body, parent_id: c.id });
      else if (what === 'edit') await reviewApi.patchComment(c.id, { body: body.body });
      else if (what === 'status') await reviewApi.patchComment(c.id, { status: body.status });
      else if (what === 'delete') await reviewApi.deleteComment(c.id);
      await loadComments();
      loadReview().catch(() => {});
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleDone = async () => {
    try {
      await reviewApi.done(reviewId, review.my_status !== 'done');
      await loadReview();
    } catch (e) {
      setError(e.message);
    }
  };

  if (error && !review) {
    return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  }
  if (!review) return <div className="drv-page"><div className="drv-muted">Opening the review…</div></div>;

  const roundNo = round || review.current_round;
  const thisRound = (review.rounds || []).find((r) => r.round_no === roundNo);
  const kind = doc?.file_kind || 'other';
  // Orcanos renders Office files and PDFs as PDF; a drawing or a zip has nothing to compare.
  const views = docError || !doc || kind === 'other' ? []
    : [['doc', 'Document'], ...(kind === 'pdf' ? [] : [['pdf', 'PDF']]), ['compare', 'Compare revisions']];
  const shownView = views.some(([k]) => k === view) ? view : 'doc';

  return (
    <div className="drv-doc">
      <header className="drv-doc-bar">
        <div className="drv-doc-title">
          <Link className="drv-btn" to={`/review/item/${encodeURIComponent(review.item_id)}?setup=${review.setup_id}&review=${review.id}`}
            aria-label="Back to the reviews of this document">←</Link>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: '#5C35A8' }}>{review.item_key}</span> {review.item_name}
            </div>
            <div className="drv-small">
              {review.name} · Rev {review.revision} · Round {roundNo}
              {thisRound?.status === 'closed' ? ' (closed)' : ''}
              {review.due_date ? ` · due ${fmtDate(review.due_date)}` : ''}
              {' · '}<FileChip fileName={doc?.file_name || review.file_name} kind={kind} />
              {review.status !== 'open' && <> · <strong>{review.status === 'complete' ? 'Review complete' : 'Review cancelled'}</strong></>}
            </div>
          </div>
        </div>
        <div className="drv-doc-tools">
          {views.length > 1 && (
            <div className="drv-seg" role="tablist" aria-label="View">
              {views.map(([k, label]) => (
                <button type="button" role="tab" key={k} aria-selected={shownView === k}
                  className={`drv-seg-btn${shownView === k ? ' drv-seg-btn--on' : ''}`}
                  onClick={() => { setView(k); setSelection(null); }}>{label}</button>
              ))}
            </div>
          )}
          {(review.rounds || []).length > 1 && (
            <label className="drv-small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Round
              <select className="drv-select" value={roundNo}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  const next = new URLSearchParams(params);
                  if (n === review.current_round) next.delete('round'); else next.set('round', String(n));
                  setParams(next);
                  setDraft(null);
                }}>
                {review.rounds.map((r) => (
                  <option key={r.round_no} value={r.round_no}>Round {r.round_no}{r.status === 'closed' ? ' · closed' : ' · open'}</option>
                ))}
              </select>
            </label>
          )}
          {docError ? null : kind === 'docx' ? (
            <>
              <button type="button" className="drv-btn" onClick={() => download(roundNo, true)}
                title="The Word file with this round's comments as Word comments">Download with comments</button>
              <button type="button" className="drv-btn" onClick={() => download(roundNo)}
                title="The draft exactly as it is in Orcanos">Original</button>
            </>
          ) : (
            <button type="button" className="drv-btn" onClick={() => download(roundNo)}>Download</button>
          )}
          {review.is_reviewer && review.status === 'open' && thisRound?.status === 'open' && (
            <button type="button" className={`drv-btn${review.my_status === 'done' ? '' : ' drv-btn--primary'}`} onClick={toggleDone}>
              {review.my_status === 'done' ? 'Not done yet' : 'I’m done reviewing'}
            </button>
          )}
        </div>
      </header>

      <div className="drv-doc-body">
        <div className="drv-doc-area" ref={docAreaRef} onMouseDown={(e) => { if (!e.target.closest('.drv-selbar')) setSelection(null); }}>
          {!doc && !docError && <div className="drv-muted" style={{ padding: 32 }}>Reading the file from Orcanos…</div>}
          {docError && (
            <div className="drv-error" role="alert" style={{ margin: 24 }}>
              {docError}{review.status === 'open' && thisRound?.status === 'open'
                ? ' You can still comment on the whole document on the right.' : ''}
            </div>
          )}
          {shownView === 'pdf' && <PdfRenditionView reviewId={reviewId} round={roundNo} />}
          {shownView === 'compare' && <CompareView reviewId={reviewId} round={roundNo} />}
          {shownView === 'doc' && <>
          {doc?.render_error && <div className="drv-note" style={{ margin: 24 }}>{doc.render_error}</div>}

          {doc && kind === 'docx' && doc.html !== undefined && (
            <>
              {canComment && <div className="drv-doc-hint">Select text to comment on it or suggest a change.</div>}
              <WordView html={doc.html} comments={comments} focusId={focusId} onPick={pick}
                onSelect={onSelect} onMissing={onMissing} />
              {selection && (
                <div className="drv-selbar" role="toolbar" aria-label="Selection actions"
                  style={{ top: selection.top, left: selection.left }}>
                  <button type="button" onClick={() => startDraft({ kind: 'comment', anchor_type: 'text', anchor: selection.anchor })}>Comment</button>
                  <button type="button" onClick={() => startDraft({ kind: 'change', anchor_type: 'text', anchor: selection.anchor })}>Suggest change</button>
                </div>
              )}
            </>
          )}

          {doc && kind === 'xlsx' && doc.sheets && (
            <div style={{ padding: 24 }}>
              {canComment && <div className="drv-doc-hint" style={{ padding: '0 0 10px' }}>Click a cell to comment on it. Formulas are shown as their values.</div>}
              <ExcelView sheets={doc.sheets} comments={comments} focusId={focusId}
                onCell={(sheet, cell, value, existing) => {
                  if (existing && !canComment) { pick(existing); return; }
                  if (!canComment) return;
                  startDraft({ kind: 'comment', anchor_type: 'cell', anchor: { sheet, cell }, label: `${sheet}!${cell}`, value });
                }} />
              {doc.truncated && <div className="drv-small" style={{ marginTop: 8 }}>Large sheet — only the first rows and columns are shown. Download it to see everything.</div>}
            </div>
          )}

          {doc && kind === 'pdf' && (
            pdfUrl
              ? <iframe className="drv-pdf" title={doc.file_name} src={`${pdfUrl}#toolbar=1`} />
              : <div className="drv-muted" style={{ padding: 32 }}>Opening the PDF…</div>
          )}

          {doc && kind === 'other' && (
            <div className="drv-other">
              <div className="drv-other-card">
                <FileChip fileName={doc.file_name} kind="other" />
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <h2 style={{ margin: 0, fontSize: 18, wordBreak: 'break-all' }}>{doc.file_name}</h2>
                  <p style={{ margin: '8px 0 0', color: '#4A4560', fontSize: 14, lineHeight: 1.55 }}>
                    This file type can’t be shown here. Download it, read it in your own viewer, and leave
                    your comments on the right. Add <em>where</em> you mean if it helps (sheet, zone, page).
                  </p>
                  <div className="drv-small" style={{ marginTop: 6 }}>{Math.round((doc.size || 0) / 1024).toLocaleString()} KB</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => download(roundNo)}>Download file</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          </>}
        </div>

        <aside className="drv-side" aria-label="Comments and properties">
          <div className="drv-side-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={side === 'comments'}
              className={`drv-side-tab${side === 'comments' ? ' drv-side-tab--on' : ''}`} onClick={() => setSide('comments')}>
              Comments <span>{counts.all}</span>
            </button>
            <button type="button" role="tab" aria-selected={side === 'props'}
              className={`drv-side-tab${side === 'props' ? ' drv-side-tab--on' : ''}`} onClick={() => setSide('props')}>
              Properties
            </button>
          </div>

          {side === 'props' ? (
            <div className="drv-side-scroll"><ItemDetails load={loadForm} /></div>
          ) : (
            <>
              <div className="drv-filter-tabs" role="tablist" aria-label="Show">
                {TABS.filter(([k]) => k !== 'earlier' || earlierTops.length > 0).map(([k, label]) => (
                  <button type="button" role="tab" key={k} aria-selected={tab === k}
                    className={`drv-ftab${tab === k ? ' drv-ftab--on' : ''}`} onClick={() => setTab(k)}>
                    {label} {counts[k]}
                  </button>
                ))}
              </div>
              <div className="drv-side-scroll" ref={listRef}>
                {error && <div className="drv-error" role="alert">{error}</div>}
                {!canComment && (
                  <div className="drv-small" style={{ marginBottom: 4 }}>
                    {review.status !== 'open' ? 'This review is closed — comments are read-only.'
                      : thisRound?.status !== 'open' ? 'This round is closed — comments are read-only.'
                        : 'You are not a reviewer of this review, so you can read comments but not add them.'}
                  </div>
                )}

                {draft && (
                  <div className="drv-cm drv-cm--on">
                    <div className="drv-small" style={{ fontWeight: 600, color: '#3D2070' }}>
                      {draft.kind === 'change' ? 'Suggest a change' : draft.anchor_type === 'cell' ? `Comment on ${draft.label}` : 'New comment'}
                    </div>
                    {draft.anchor_type === 'text' && draft.kind === 'comment' && <div className="drv-quote">“{draft.anchor.selected_text}”</div>}
                    {draft.anchor_type === 'cell' && draft.value && <div className="drv-quote">{draft.value}</div>}
                    {draft.kind === 'change' && (
                      <>
                        <div className="drv-quote"><span className="drv-del">{draft.anchor.selected_text}</span></div>
                        <label className="drv-field">
                          <span className="drv-lbl">Replace with</span>
                          <textarea className="drv-textarea" style={{ minHeight: 56 }} value={draftChange}
                            onChange={(e) => setDraftChange(e.target.value)} autoFocus />
                        </label>
                      </>
                    )}
                    <textarea className="drv-textarea" style={{ minHeight: 64 }} value={draftBody}
                      aria-label="Your comment" autoFocus={draft.kind !== 'change'}
                      placeholder={draft.kind === 'change' ? 'Why? (optional)' : 'Write your comment…'}
                      onChange={(e) => setDraftBody(e.target.value)} />
                    <div className="drv-cm-row">
                      <button type="button" className="drv-btn" onClick={() => setDraft(null)}>Cancel</button>
                      <button type="button" className="drv-btn drv-btn--primary" onClick={submitDraft}
                        disabled={busy || (draft.kind === 'change' ? !draftChange.trim() || draftChange === draft.anchor.selected_text : !draftBody.trim())}>
                        {draft.kind === 'change' ? 'Suggest' : 'Add comment'}
                      </button>
                    </div>
                  </div>
                )}

                {shown.length === 0 && !draft && (
                  <div className="drv-muted" style={{ fontSize: 13, padding: '8px 0' }}>
                    {tab === 'all' ? 'No comments yet.' : 'Nothing here.'}
                  </div>
                )}
                {tab === 'earlier' && shown.length > 0 && (
                  <div className="drv-small" style={{ marginBottom: 4 }}>
                    Comments from earlier rounds, written on an earlier draft. You can still reply to them and resolve them.
                  </div>
                )}
                {shown.map((c, i) => (
                  <React.Fragment key={c.id}>
                    {tab === 'earlier' && c.round_no !== shown[i - 1]?.round_no && (
                      <div className="drv-lbl" style={{ margin: '8px 0 2px' }}>Round {c.round_no}</div>
                    )}
                    <CommentThread comment={c} replies={repliesOf[c.id] || []} focused={c.id === focusId}
                      canComment={canComment} missing={tab !== 'earlier' && missing.includes(c.id)}
                      onFocus={(id) => { setFocusId(id); }} onAction={onAction} />
                  </React.Fragment>
                ))}
              </div>

              {canComment && (
                <div className="drv-side-compose">
                  <label className="drv-lbl" htmlFor="drv-general">Comment on the whole document</label>
                  {(kind === 'pdf' || kind === 'other' || kind === 'xlsx') && (
                    <input className="drv-input" style={{ height: 34, fontSize: 13 }} value={where}
                      onChange={(e) => setWhere(e.target.value)}
                      placeholder={kind === 'pdf' ? 'Page (optional)' : 'Where (optional) — e.g. Sheet 2, zone C4'} aria-label="Where" />
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <textarea id="drv-general" className="drv-textarea" style={{ minHeight: 40, flex: 1 }} value={general}
                      onChange={(e) => setGeneral(e.target.value)} placeholder="Write a general comment…" />
                    <button type="button" className="drv-btn drv-btn--primary" style={{ alignSelf: 'flex-end' }}
                      disabled={busy || !general.trim()} onClick={submitGeneral}>Send</button>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
